import React, { useState, useEffect } from "react";
import { profileAPI } from "../services/api";
import "../css/Reviews.css";
import { ShimmerBox } from "../components/Shimmer";
import "../css/Shimmer.css";
import ratingsEmptyIcon from '../assets/images/EmptyIcons/Ratings-eampty.svg';

const Reviews = () => {
  const [sortBy, setSortBy] = useState("trip_id");
  const [tableLoading, setTableLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  // Separate state for overall ratings (loaded once) and reviews (changes with sort)
  const [overallRatingsData, setOverallRatingsData] = useState(null);
  const [reviewsList, setReviewsList] = useState([]);

  // Fetch initial data (overall ratings + reviews)
  const fetchInitialData = async () => {
    try {
      setInitialLoading(true);
      setError(null);
      const response = await profileAPI.getProviderReviews({ page: 1 });
      if (response.data.Status === 'Success') {
        // Set rating summary from OverallRatings
        if (response.data.OverallRatings) {
          const overall = response.data.OverallRatings;
          const breakdown = overall.Breakdown || [];
          const total = overall.TotalReviews || 0;

          // Map breakdown array to counts
          const getStarData = (stars) => breakdown.find(b => b.Stars === stars) || { Count: 0, Percentage: 0 };

          setOverallRatingsData({
            TotalReviews: total,
            FiveStarCount: getStarData(5).Count,
            FiveStarPercent: getStarData(5).Percentage,
            FourStarCount: getStarData(4).Count,
            FourStarPercent: getStarData(4).Percentage,
            ThreeStarCount: getStarData(3).Count,
            ThreeStarPercent: getStarData(3).Percentage,
            TwoStarCount: getStarData(2).Count,
            TwoStarPercent: getStarData(2).Percentage,
            OneStarCount: getStarData(1).Count,
            OneStarPercent: getStarData(1).Percentage,
          });
        }
        // Set reviews list
        setReviewsList(response.data.Reviews || []);
        setPagination(response.data.Pagination || null);
      } else {
        setOverallRatingsData(null);
        setReviewsList([]);
      }
    } catch (err) {
      setOverallRatingsData(null);
      setReviewsList([]);
    } finally {
      setInitialLoading(false);
    }
  };

  // Fetch reviews with pagination and sort
  const fetchReviews = async (page = 1, sort = sortBy) => {
    try {
      setTableLoading(true);
      const response = await profileAPI.getProviderReviews({ page, sort_by: sort });
      if (response.data.Status === 'Success') {
        setReviewsList(response.data.Reviews || []);
        setPagination(response.data.Pagination || null);
      }
    } catch (err) {
      // Error fetching reviews
    } finally {
      setTableLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Page change - reload table
  useEffect(() => {
    if (!initialLoading && currentPage > 1) {
      fetchReviews(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Handle sort change
  const handleSortChange = (e) => {
    const newSort = e.target.value;
    setSortBy(newSort);
    setCurrentPage(1);
    fetchReviews(1, newSort);
  };

  // Transform overall ratings data
  const overallRatings = overallRatingsData ? {
    totalReviews: overallRatingsData.TotalReviews || 0,
    ratings: [
      { stars: 5, count: overallRatingsData.FiveStarCount || 0, percentage: overallRatingsData.FiveStarPercent || 0 },
      { stars: 4, count: overallRatingsData.FourStarCount || 0, percentage: overallRatingsData.FourStarPercent || 0 },
      { stars: 3, count: overallRatingsData.ThreeStarCount || 0, percentage: overallRatingsData.ThreeStarPercent || 0 },
      { stars: 2, count: overallRatingsData.TwoStarCount || 0, percentage: overallRatingsData.TwoStarPercent || 0 },
      { stars: 1, count: overallRatingsData.OneStarCount || 0, percentage: overallRatingsData.OneStarPercent || 0 },
    ],
  } : null;

  // Transform reviews array - map API fields to display fields
  const customerRatings = reviewsList?.map(review => ({
    bookingId: review.BookingNumber ? `#${review.BookingNumber}` : `#${review.BookingId || 'N/A'}`,
    assignedName: review.DriverName || review.Assigned?.Name || 'N/A',
    assignedImage: review.DriverPhoto || review.Assigned?.Photo || null,
    customer: review.CustomerName || 'N/A',
    service: review.Service || review.ServiceName || 'N/A',
    rating: review.Rating || review.DriverRating || 0,
    stars: Math.round(review.Rating || review.DriverRating || 0),
    comment: review.Comments || review.Review || '',
  })) || [];

  
  // Render stars
  const renderStars = (count) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <span key={index} className={`reviews-star ${index < count ? "reviews-star-filled" : ""}`}>
        ★
      </span>
    ));
  };

  // Initial loading state - Shimmer
  if (initialLoading) {
    return (
      <div className="reviews-page">
        <div className="container-fluid px-4 py-4">
          {/* Overall Ratings Shimmer */}
          <div className="reviews-overall-card">
            <div className="reviews-overall-header">
              <ShimmerBox width="150px" height="24px" />
              <ShimmerBox width="100px" height="18px" />
            </div>
            <div className="reviews-bars">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="reviews-bar-row">
                  <ShimmerBox width="30px" height="16px" />
                  <div className="reviews-bar-track">
                    <ShimmerBox width={`${90 - item * 15}%`} height="100%" borderRadius="10px" />
                  </div>
                  <ShimmerBox width="50px" height="16px" />
                </div>
              ))}
            </div>
          </div>

          {/* Customer Ratings Header Shimmer */}
          <div className="reviews-customer-header">
            <ShimmerBox width="180px" height="24px" />
            <ShimmerBox width="140px" height="40px" borderRadius="8px" />
          </div>

          {/* Table Shimmer */}
          <div className="reviews-table-card">
            <div className="reviews-table-header">
              <ShimmerBox width="80px" height="14px" />
              <ShimmerBox width="80px" height="14px" />
              <ShimmerBox width="80px" height="14px" />
              <ShimmerBox width="80px" height="14px" />
              <ShimmerBox width="80px" height="14px" />
              <ShimmerBox width="100px" height="14px" />
            </div>
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="reviews-table-row">
                <div className="reviews-col reviews-col-booking">
                  <ShimmerBox width="60px" height="14px" />
                </div>
                <div className="reviews-col reviews-col-assigned">
                  <div className="reviews-assigned-info">
                    <ShimmerBox width="40px" height="40px" borderRadius="50%" />
                    <ShimmerBox width="80px" height="14px" />
                  </div>
                </div>
                <div className="reviews-col reviews-col-customer">
                  <ShimmerBox width="90px" height="14px" />
                </div>
                <div className="reviews-col reviews-col-service">
                  <ShimmerBox width="80px" height="14px" />
                </div>
                <div className="reviews-col reviews-col-ratings">
                  <ShimmerBox width="100px" height="16px" />
                </div>
                <div className="reviews-col reviews-col-comments">
                  <ShimmerBox width="150px" height="14px" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="reviews-page">
        <div className="container-fluid px-4 py-4">
          <div className="reviews-error-container">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <h3>Failed to load reviews</h3>
            <p>{error}</p>
            <button className="retry-btn" onClick={() => fetchInitialData()}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="reviews-page">
      <div className="container-fluid px-4 py-4">

        {/* Overall Ratings Card */}
        <div className="reviews-overall-card">
          <div className="reviews-overall-header">
            <h2 className="reviews-overall-title">Overall Ratings</h2>
            <span className="reviews-overall-count">{overallRatings?.totalReviews?.toLocaleString() || 0} Reviews</span>
          </div>

          <div className="reviews-bars">
            {overallRatings?.ratings?.map((rating) => (
              <div key={rating.stars} className="reviews-bar-row">
                <div className="reviews-bar-label">
                  <span className="reviews-bar-stars">{rating.stars}</span>
                  <span className="reviews-bar-icon">★</span>
                </div>
                <div className="reviews-bar-track">
                  <div
                    className={`reviews-bar-fill reviews-bar-fill-${rating.stars}`}
                    style={{ width: `${rating.percentage}%` }}
                  ></div>
                </div>
                <div className="reviews-bar-count">{rating.count.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Customer Ratings Header - Outside card */}
        <div className="reviews-customer-header">
          <h2 className="reviews-customer-title">Customer Ratings</h2>
          <div className="reviews-sort-dropdown">
            <label className="reviews-sort-label">Sort by</label>
            <select
              className="reviews-sort-select"
              value={sortBy}
              onChange={handleSortChange}
            >
              <option value="trip_id">Trip Id</option>
              <option value="highest_rating">Highest Rating</option>
              <option value="lowest_rating">Lowest Rating</option>
            </select>
          </div>
        </div>

        {/* Customer Ratings Table Card */}
        <div className="reviews-table-card">
          {/* Table Header */}
          <div className="reviews-table-header">
            <div className="reviews-col reviews-col-booking">Booking Id</div>
            <div className="reviews-col reviews-col-assigned">Assigned</div>
            <div className="reviews-col reviews-col-customer">Customer</div>
            <div className="reviews-col reviews-col-service">Service</div>
            <div className="reviews-col reviews-col-ratings">Ratings</div>
            <div className="reviews-col reviews-col-comments">Comments</div>
          </div>

          {/* Table Body */}
          <div className={`reviews-table-body ${tableLoading ? 'loading' : ''}`}>
            {tableLoading ? (
              <div className="reviews-table-loading">
                <div className="spinner"></div>
              </div>
            ) : customerRatings.length === 0 ? (
              <div className="reviews-empty-container">
                <img src={ratingsEmptyIcon} alt="No Reviews" className="empty-state-icon" />
                <h3>No Reviews Yet</h3>
                <p>Customer reviews will appear here once received.</p>
              </div>
            ) : (
              customerRatings.map((rating, index) => (
                <div key={index} className="reviews-table-row">
                  <div className="reviews-col reviews-col-booking">
                    <span className="reviews-booking-id">{rating.bookingId}</span>
                  </div>

                  <div className="reviews-col reviews-col-assigned">
                    <div className="reviews-assigned-info">
                      {rating.assignedImage ? (
                        <img src={rating.assignedImage} alt={rating.assignedName} className="reviews-assigned-avatar" />
                      ) : (
                        <div className="reviews-assigned-avatar reviews-avatar-placeholder">
                          {rating.assignedName?.charAt(0)?.toUpperCase() || 'D'}
                        </div>
                      )}
                      <span className="reviews-assigned-name">{rating.assignedName}</span>
                    </div>
                  </div>

                  <div className="reviews-col reviews-col-customer">
                    <span className="reviews-customer-name">{rating.customer}</span>
                  </div>

                  <div className="reviews-col reviews-col-service">
                    <span className="reviews-service-name">{rating.service}</span>
                  </div>

                  <div className="reviews-col reviews-col-ratings">
                    {rating.rating > 0 ? (
                      <div className="reviews-rating-display">
                        <div className="reviews-stars-display">
                          {renderStars(rating.stars)}
                        </div>
                        <span className="reviews-rating-text">Rated {rating.rating} out of 5</span>
                      </div>
                    ) : (
                      <div className="reviews-rating-display">
                        <div className="reviews-stars-display reviews-stars-empty">
                          {renderStars(0)}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="reviews-col reviews-col-comments">
                    <p className="reviews-comment-text">{rating.comment || '-'}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.TotalPages > 1 && (
            <div className="reviews-pagination">
              <button
                className="pagination-btn"
                disabled={!pagination.HasPrevious}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.CurrentPage} of {pagination.TotalPages}
              </span>
              <button
                className="pagination-btn"
                disabled={!pagination.HasNext}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Reviews;